/**
 * 머큐리 보험 정산 소속 (화물공제조합 '022002' · KB손해보험 '022003') 발주 전략
 *
 * 보험건 체크 시 보험사/팀/담당자/접수번호/계산서 발행 메타를 받는다.
 * 접수번호를 외부 API(/m/ajax/mercury/insurance-search)로 조회하면 나머지가 전부 자동으로 채워진다.
 *
 * ⚠ 나머지 입력칸(「보험사 정보」 박스, `.insurance-group`)은 처음엔 회색 잠금이다.
 *    잠긴 채 빈 칸이 먼저 보이면 발주자는 자동조회가 있는 줄 모르고 손으로 다 채운다 — 실제 CS 접수 사유다.
 *    그래서 박스는 셋 중 하나로만 등장한다 —
 *      · IDLE   접수번호만 있고 박스는 잠금(회색·비활성) — 잠긴 4칸을 그대로 보여주는 대신
 *               필드 위에 "조회하면 자동 입력" 오버레이를 덮어 한 문장으로 말한다(`_setOverlay`).
 *               헤더 오른쪽엔 [번호 없으면 직접 입력하기] 링크
 *      · AUTO   조회 성공, 값은 채웠지만 여전히 잠금(흰 배경+굵게로 "채워짐"만 표시) — 오버레이는
 *               사라지고 실제 값이 드러난다. "불러왔습니다"는 토스트로만 말한다(`_fill()`).
 *               RNS 가 준 값을 실수로 고쳐 쓰지 못하게 막는다 — 고치려면 헤더의
 *               [내용이 다르면 고쳐 쓰기] 링크를 눌러 MANUAL 로 넘어가야 한다
 *      · MANUAL 헤더 링크 클릭 또는 저장된 오더 로드 — 박스 잠금 해제, 오버레이도 사라지고
 *               사람이 직접 관리. 헤더 링크는 더 보여줄 게 없어 숨긴다
 */
import Base from './Base';
import Util from '../../../Common/Util';

const REQ_FIELDS = [
    { id: '#input_meta_receipt_no',     name: '접수번호' },
    { id: '#input_meta_insurance_name', name: '보험사명' },
    { id: '#input_meta_team_name',      name: '보상팀명' },
    { id: '#input_meta_manager_name',   name: '보상담당자명' },
    { id: '#input_meta_manager_phone',  name: '담당자 연락처' },
];

/**
 * 나머지 칸이 어떻게 채워졌는지 — blur 자동조회가 사람이 친 값을 덮어써도 되는지의 판정 근거이자
 * 「보험사 정보」 박스의 잠금 상태·헤더 문구를 정하는 근거다.
 *   IDLE   접수번호만 있는 상태 (박스 잠금)
 *   AUTO   조회로 채워짐, 박스는 여전히 잠금(filled 스타일) → 접수번호가 바뀌면 다시 조회해
 *          덮어써도 잃을 게 없다. [고쳐 쓰기]를 누르면 MANUAL 로 넘어간다
 *   MANUAL 사람이 직접 입력했거나(빈 칸) [고쳐 쓰기]로 잠금을 풀었거나(값 유지) 저장된 오더를
 *          불러온 상태 → 자동으로 덮어쓰지 않는다. 다시 부르려면 [조회]/[번호로 불러오기]를
 *          명시적으로 눌러야 한다
 */
const MODE = { IDLE: 'idle', AUTO: 'auto', MANUAL: 'manual' };

// 필드 위 오버레이의 자물쇠 아이콘은 상태 무관 고정이라 blade 에 정적으로 박혀있다
// (baljoo.blade.php `.insurance-group-overlay`) — 여기서 JS 로 채울 필요가 없다.
// 에러줄 전용 — 경고 원.
const IC_ERR  = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.4h.01"/></svg>';

// 헤더 우측 링크 버튼 문구 — idle/error 는 [직접 입력]으로, ok 는 [고쳐 쓰기]로, 셋 다 MANUAL 로
// 전환한다. MANUAL(이미 편집 중)은 더 보여줄 링크가 없어 버튼 자체를 감춘다 — `_applyMode()` 참고.
const LINK = {
    idle:  '번호 없으면 직접 입력하기 ›',
    ok:    '내용이 다르면 고쳐 쓰기 ›',
    error: '직접 입력하기 ›',
};

export default class Mercury extends Base {
    constructor() {
        super();
        this.mode = MODE.IDLE;
        this.busy = false;
    }

    matches(sosok) {
        const code = (sosok || '').substring(0, 6);
        return code === '022002' || code === '022003';  // 화물공제조합·KB손해보험
    }

    // ─── 화면 초기화 ────────────────────────────────
    onInitPage() {
        $('.trade-contact').hide();
        $('.field-contact').hide();
        // insurance_ck_box만 노출, 나머지(에러줄·보험사정보박스·링크줄)는 체크 시 노출
        $('.mercury-insurance.insurance_ck_box').css('display', 'flex');
        $('.mercury-insurance').not('.insurance_ck_box').hide();

        this.mode = MODE.IDLE;
        this.busy = false;
        this._setBusy(false);
        this._setErrorLine('');
        this._applyMode();

        const $check = $('.insurance_ck_box input[type="checkbox"]');
        // 머큐리 보험 정산 소속(화물공제조합·KB손해보험)은 대부분 보험건이라 신규발주 시 기본으로 체크해둔다.
        // 기존 오더를 불러오는 경우(재발주·오더복사·오더수정)는 뒤이어 호출되는 bindMeta 가 저장된 값으로 다시 판정한다.
        if (editMode === '') {
            $check.prop('checked', true);
            this._onInsuranceToggle(true, { focus: false });
        } else {
            $('#input_meta_receipt_no').prop('disabled', !$check.is(':checked'));
        }
    }

    // ─── 이벤트 핸들러 ──────────────────────────────
    onInitHandler(/* baljoo */) {
        $j.Common.Form.registerPhoneHandler('input_meta_manager_phone');

        // $j.reset 으로 페이지 모듈만 다시 만들어도 전략은 모듈 싱글턴이라 살아있다 → 중복 바인딩 방지
        const $receipt = $('#input_meta_receipt_no').off('.mercury');

        // 접수번호 입력 후 blur → 보험사 정보 자동 조회 (MANUAL 이면 사람이 친 값을 지키느라 건너뛴다)
        $receipt.on('blur.mercury', () => this._search({ force: false }));
        $receipt.on('keydown.mercury', (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            $receipt.blur();          // 키보드를 내려 결과 문구가 가리지 않게 한다
            this._search({ force: true });
        });
        // 조회 버튼 — 같은 번호여도, MANUAL 이어도 무조건 다시 부른다 (명시적 행위)
        $('#insurance_search_btn').off('.mercury').on('click.mercury', () => this._search({ force: true }));

        // 박스 밖 링크줄 — [직접 입력]/[고쳐 쓰기] 문구만 다를 뿐 항상 MANUAL 로 전환한다 (LINK 참고)
        $('.insurance-manual-btn').off('.mercury').on('click.mercury', () => this._enterManual());

        // 계산서 발행 토글
        $('input[name="meta_invoice"]').off('.mercury').on('change.mercury', this._onInvoiceToggle);

        // 보험건 체크박스 토글
        $('.insurance_ck_box input[type="checkbox"]').off('.mercury')
            .on('change.mercury', (e) => this._onInsuranceToggle($(e.currentTarget).is(':checked')));
    }

    // ─── 「보험사 정보」 박스 — 잠금·헤더 상태 ───────────────
    /**
     * 박스 잠금(비활성) on/off. 박스 자체의 노출 여부는 건드리지 않는다.
     * @param {boolean} filled 조회로 채워진 값이라 잠긴 채로도 "채워졌다"를 보여줘야 하는 경우(AUTO).
     *   인풋만 흰 배경+굵게로 바뀐다 — 주소록 버튼은 여전히 잠금 모양 그대로(시안 그대로).
     *   내용을 고치려면 [내용이 다르면 고쳐 쓰기]로 MANUAL 전환해야 한다.
     */
    _setLock(locked, filled) {
        $('#insurance_group').toggleClass('locked', locked).toggleClass('filled', !!filled);
        $('.insurance-group-bd input, .insurance-group-bd button, .insurance-detail input')
            .prop('disabled', locked);
    }

    /**
     * 필드 위 오버레이 — "조회하면 자동 입력"을 잠긴 4칸 대신 한 문장으로 덮어 보여준다.
     * IDLE(조회 전/실패)에서만 뜨고, AUTO(채워짐)·MANUAL(편집중)로 넘어가면 사라져
     * 실제 필드가 드러난다.
     */
    _setOverlay(show) {
        $('#insurance_group_overlay').toggle(show);
    }

    /** 헤더 우측 링크 버튼. kind: 'idle' | 'ok' | 'error' | null(숨김) — LINK 참고 */
    _setLinkLine(kind) {
        if (!kind) { $('.insurance-manual-btn').hide(); return; }
        $('.insurance-manual-btn').show().text(LINK[kind] || LINK.idle);
    }

    /** 현재 mode 에 맞춰 박스 잠금·오버레이·헤더 링크를 한 번에 맞춘다 */
    _applyMode() {
        if (this.mode === MODE.AUTO) {
            // 조회 성공 — 값은 채웠지만 여전히 잠긴 채(disabled)로 둔다. RNS 가 준 값을
            // 실수로 고쳐 쓰지 못하게 하려는 것 — 고치려면 [내용이 다르면 고쳐 쓰기]로
            // MANUAL 전환을 명시적으로 거쳐야 한다.
            this._setLock(true, true);
            this._setOverlay(false);
            this._setLinkLine('ok');
        } else if (this.mode === MODE.MANUAL) {
            this._setLock(false);
            this._setOverlay(false);
            // [직접 입력하기]를 한 번 누르면 그 버튼은 아예 사라진다 — 다시 조회하려면
            // 접수번호를 고쳐 [조회]를 누르면 된다. 새로고침(재초기화)해서 IDLE 로 돌아가야
            // 링크가 다시 뜬다 (onInitPage 참고).
            this._setLinkLine(null);
        } else {
            this._setLock(true);
            this._setOverlay(true);
            this._setLinkLine('idle');
        }
    }

    /** 에러줄. 빈 문자열이면 감춘다 (토스트 아님 — 입력칸 바로 아래 인라인 문구) */
    _setErrorLine(text) {
        const show = !!text;
        $('.insurance-error-line').css('display', show ? 'flex' : 'none');   // flex 줄 — toggle() 은 block 으로 연다
        if (show) {
            $('.insurance-error-ic').html(IC_ERR);
            $('.insurance-error-tx').text(text);
        }
    }

    _setBusy(busy) {
        this.busy = busy;
        $('#insurance_search_btn').prop('disabled', busy).text(busy ? '조회중' : '조회');
    }

    _clearFields() {
        $('#input_meta_insurance_name').val('');
        $('#input_meta_team_name').val('');
        $('#input_meta_manager_name').val('');
        $('#input_meta_manager_phone').val('');
        $('#input_meta_addr1').val('');
        $('#input_meta_addr2').val('');
        $('input[name="meta_invoice"]').prop('checked', false).trigger('change');
    }

    // ─── 관리자 전용 주소칸(기본주소·세부주소) 노출 ───────────
    /** `.insurance-detail`(관리자 그룹의 addr1/addr2)만 대상. 보험사 정보 박스는 `_setLock()`이 담당 */
    _setAdminVisible(show) {
        const on = show && $('#insurance_check').is(':checked');
        $('.insurance-detail').each(function () {
            // .contract(주소록 버튼)를 품은 줄은 block, 나머지는 flex — 원래 규칙 그대로
            if (on) $(this).css('display', $(this).find('.contract').length ? 'block' : 'flex');
            else    $(this).hide();
        });
    }

    // ─── 조회 ────────────────────────────────────────
    /**
     * @param {boolean} force 버튼/엔터로 부른 명시적 조회. blur 자동조회는 false.
     */
    _search({ force }) {
        if (this.busy) return;
        if (!$('#insurance_check').is(':checked')) return;

        const $input = $('#input_meta_receipt_no');
        const code = ($input.val() || '').replace(/[^0-9A-Za-z]/g, '');

        if (!force) {
            if (this.mode === MODE.MANUAL) return;          // 사람이 친 값을 몰래 덮지 않는다
            if ($input.data('lastSearched') === code) return; // 번호가 그대로면 다시 부르지 않는다
        }
        $input.data('lastSearched', code);

        if (code.length < 5) {
            // 번호가 비었거나 규격에 안 맞는다 — 조회 자체를 시도하지 않지만, 사유를
            // 따로 안내(예: "접수번호를 입력해주세요")하지 않고 실제 조회 실패와 같은
            // "번호를 찾지 못했습니다"로 통일한다. 형식 오류만 다른 문구로 갈리면
            // 사용자 입장에선 원인이 또 있는 것처럼 보인다 — 시안 그대로 사유 불문 한 문구.
            this._clearFields();
            this.mode = MODE.IDLE;
            this._setAdminVisible(false);
            this._applyMode();
            if (force) this._fail(); else this._setErrorLine('');
            return;
        }

        this._setBusy(true);
        this._setErrorLine('');

        $.ajax({
            type: 'GET',
            url:  '/m/ajax/mercury/insurance-search',
            data: { insu_code: code },
            success: (r) => {
                this._setBusy(false);
                if (r.result && r.data) {
                    this._fill(r.data);
                    return;
                }
                this._fail();
            },
            error: () => {
                this._setBusy(false);
                $input.removeData('lastSearched');
                this._fail();
            },
        });
    }

    _fill(d) {
        $('#input_meta_insurance_name').val(d.insu_name || '');
        $('#input_meta_team_name').val(d.position || '');
        $('#input_meta_manager_name').val(d.name || '');
        $('#input_meta_manager_phone').val(Util.phoneNumber(d.pcs || ''));
        $('#input_meta_addr1').val(d.addr1 || '');
        $('#input_meta_addr2').val(d.addr2 || '');
        // 계산서 발행은 RNS 응답(vatyn: '유'/'무')을 따른다 — 보험사 쪽 기록이 정본이고, 어긋나면
        // 작업완료 청구서의 vat 와 수수료 특례(미발행이면 선차감율)가 보험사 기록과 갈린다.
        // 오더수정에서 [조회]를 다시 누르면 저장값 대신 이 값으로 덮인다(그게 조회의 뜻이다).
        // 발주자가 다르게 알고 있으면 조회 뒤 박스 밖 토글로 고친다.
        const isVat = (d.vatyn || '').trim() === '유';
        $('input[name="meta_invoice"]').prop('checked', isVat).trigger('change');

        this.mode = MODE.AUTO;
        this._setAdminVisible(true);
        this._applyMode();
        this._setErrorLine('');
        // theme:'green' — 모바일 기본 테마(dark)는 type:'success' 여도 배경이 그대로 진회색(#222)이라
        // 체크 아이콘만 붙는다. 초록 바탕이 필요하면 theme 을 명시해야 한다 (Ui/Toast.js 머리말 참고).
        $j.Ui.Toast.show('불러왔습니다.', { type: 'success', theme: 'green', closable: true });
    }

    _fail() {
        // 실패했다고 이미 풀린 잠금을 도로 걸지 않는다 — 사람이 채워둔 값이 사라지는 것처럼 보인다.
        // (AUTO 였던 경우만 초기화 — MANUAL 은 사람이 지금 채우는 중이므로 건드리지 않는다)
        if (this.mode === MODE.AUTO) {
            this._clearFields();
            this.mode = MODE.IDLE;
            this._setAdminVisible(false);
            this._applyMode();
        }
        // 시안 그대로 — 실패 사유를 가리지 않고 이 문구 하나로 통일한다. [직접 입력하기]는
        // 링크줄이 이미 말해주므로 에러줄에 안내를 더 붙이지 않는다.
        this._setErrorLine('번호를 찾지 못했습니다');
        // IDLE 로 끝났다면 링크줄도 "직접 입력하기"(에러 전용 문구)로 — idle 기본 문구보다 더 재촉한다
        if (this.mode === MODE.IDLE) this._setLinkLine('error');
    }

    _enterManual() {
        this.mode = MODE.MANUAL;
        this._setAdminVisible(true);
        this._applyMode();
        this._setErrorLine('');
        $('#input_meta_insurance_name').focus();
    }

    // ─── 토글 ────────────────────────────────────────
    _onInvoiceToggle() {
        const $label = $('.bill-status-label');
        if ($(this).is(':checked')) $label.text('발행').addClass('on');
        else                        $label.text('미발행').removeClass('on');
    }

    /** @param {boolean} isChecked @param {{focus?: boolean}} opt 초기화 시 자동 체크는 키보드를 띄우지 않는다 */
    _onInsuranceToggle(isChecked, { focus = true } = {}) {
        $('#input_meta_receipt_no').prop('disabled', !isChecked);

        if (!isChecked) {
            // mode 는 남겨둔다 — 다시 체크했을 때 이미 채운 값이 그대로 살아나야 한다
            $('.insurance-group, .insurance-error-line, .insurance_bill_box').hide();
            return;
        }
        $('.insurance-group').show();
        // 계산서 발행은 「보험사 정보」 박스 밖 — 조회 잠금과 무관하게 체크 즉시 조작 가능하다.
        // .insurance_bill_box 는 자체 CSS 가 display:flex 라 show() 로 두면 inline block 이 덮어써 layout 이 깨진다.
        $('.insurance_bill_box').css('display', 'flex');
        this._setAdminVisible(this.mode !== MODE.IDLE);
        this._applyMode();
        if (focus) $('#input_meta_receipt_no').focus();
    }

    // ─── 데이터 바인딩 ──────────────────────────────
    bindMeta(order) {
        if (!order.meta) return;
        const m = typeof order.meta === 'string' ? JSON.parse(order.meta) : order.meta;

        $('#input_meta_insurance_name').val(m.insurance_name || '');
        $('#input_meta_receipt_no').val(m.receipt_no || '');
        $('#input_meta_team_name').val(m.team_name || '');
        $('#input_meta_manager_name').val(m.manager_name || '');
        $('#input_meta_manager_phone').val(Util.phoneNumber(m.manager_phone || ''));
        $('#input_meta_addr1').val(m.addr1 || '');
        $('#input_meta_addr2').val(m.addr2 || '');
        // 계산서 발행은 '수정'(myorderedit)에서만 원본 값을 물려받는다. 오더복사·추가발주(otherorder)는
        // 새 오더라 원본이 발행이었어도 기본(미발행)에서 다시 시작해야 한다 — 발주자가 매번
        // 새로 판단할 문제이지, 복사됐다고 조용히 넘어오면 안 된다.
        if (m.invoice == '1' && editMode === 'myorderedit') {
            $('input[name="meta_invoice"]').prop('checked', true);
            $('.bill-status-label').text('발행').addClass('on');
        }

        // 보험건 체크 복원
        if (!m.insurance_name && !m.receipt_no) return;
        $('.insurance_ck_box input[type="checkbox"]').prop('checked', true);
        $('#input_meta_receipt_no').prop('disabled', false).data('lastSearched',
            (m.receipt_no || '').replace(/[^0-9A-Za-z]/g, ''));

        // 저장된 값은 사람이 손본 것일 수 있다 → MANUAL 로 두어 blur 자동조회가 덮지 못하게 한다
        this.mode = MODE.MANUAL;
        $('.insurance-group').show();
        $('.insurance_bill_box').css('display', 'flex');
        this._setAdminVisible(true);
        this._applyMode();
    }

    // ─── 검증 ────────────────────────────────────────
    validateMeta() {
        if (!$('#insurance_check').is(':checked')) return { ok: true };

        if (!$('#input_meta_receipt_no').val().trim()) {
            return { ok: false, msg: '접수번호을(를) 입력해주세요.', focus: '#input_meta_receipt_no' };
        }
        // 박스가 아직 잠긴 상태. 잠긴 칸을 가리키며 '입력하세요' 하면 아무 일도
        // 안 일어난 것처럼 보이므로, 여기서는 다음에 눌러야 할 것을 말한다.
        if (this.mode === MODE.IDLE) {
            return {
                ok: false,
                msg: '[조회]를 눌러 보험사 정보를 불러오거나, [직접 입력]으로 채워주세요.',
                focus: '#input_meta_receipt_no',
            };
        }
        for (const f of REQ_FIELDS) {
            if (!$(f.id).val().trim()) {
                return { ok: false, msg: `${f.name}을(를) 입력해주세요.`, focus: f.id };
            }
        }
        return { ok: true };
    }

    // ─── ORDER 적용 ─────────────────────────────────
    applyMetaToOrder(order) {
        if ($('#insurance_check').is(':checked')) {
            order.meta = JSON.stringify({
                invoice:        $('input[name="meta_invoice"]').is(':checked') ? '1' : '0',
                insurance_name: $('#input_meta_insurance_name').val(),
                receipt_no:     $('#input_meta_receipt_no').val().replace(/[^0-9]/g, ''),
                team_name:      $('#input_meta_team_name').val(),
                manager_name:   $('#input_meta_manager_name').val(),
                manager_phone:  $('#input_meta_manager_phone').val().replace(/[^0-9]/g, ''),
                addr1:          $('#input_meta_addr1').val(),
                addr2:          $('#input_meta_addr2').val(),
            });
            order.howtopay = 1; // 보험건이면 싸인
        } else {
            order.howtopay = 2; // 보험건 아니면 직수
        }
    }
}
